import { toAddressType, toAddressTypeDto, } from '../../address/addresses.interface';
import { toDappAddress } from '../dapp/data-service-dapp-addresses';
import { ResourceNotFoundError } from '../../sdk/errors';
import { withErrorParsing } from '../../dialect-cloud-api/data-service-errors';
import { UnencryptedTextSerde } from '../../messaging/text-serde';
export class DataServiceWallets {
    constructor(address, dataServiceWalletAddressesApi, dataServiceWalletDappAddressesApi, dataServiceWalletMessagesApi, dataServiceWalletNotificationSubscriptionsApi, dataServicePushWalletNotificationSubscriptionsApi) {
        this.address = address;
        this.dataServiceWalletAddressesApi = dataServiceWalletAddressesApi;
        this.dataServiceWalletDappAddressesApi = dataServiceWalletDappAddressesApi;
        this.dataServiceWalletMessagesApi = dataServiceWalletMessagesApi;
        this.dataServiceWalletNotificationSubscriptionsApi = dataServiceWalletNotificationSubscriptionsApi;
        this.dataServicePushWalletNotificationSubscriptionsApi = dataServicePushWalletNotificationSubscriptionsApi;
        this.addresses = new DataServiceWalletAddresses(dataServiceWalletAddressesApi);
        this.dappAddresses = new DataServiceWalletDappAddresses(dataServiceWalletDappAddressesApi);
        this.messages = new DataServiceWalletMessages(dataServiceWalletMessagesApi);
        this.notificationSubscriptions =
            new DataServiceWalletNotificationSubscriptions(dataServiceWalletNotificationSubscriptionsApi);
        this.pushNotificationSubscriptions =
            new DataServiceWalletPushNotificationSubscriptions(dataServicePushWalletNotificationSubscriptionsApi);
    }
}
export class DataServiceWalletAddresses {
    constructor(api) {
        this.api = api;
    }
    async create(command) {
        const created = await withErrorParsing(this.api.create({
            value: command.value,
            type: toAddressTypeDto(command.type),
        }));
        return toAddress(created);
    }
    async delete(command) {
        return withErrorParsing(this.api.delete(command.addressId));
    }
    async find(query) {
        try {
            const addressDto = await withErrorParsing(this.api.find(query.addressId));
            return toAddress(addressDto);
        }
        catch (e) {
            const err = e;
            if (err instanceof ResourceNotFoundError)
                return null;
            throw e;
        }
    }
    async findAll() {
        const addressDtos = await withErrorParsing(this.api.findAll());
        return addressDtos.map((it) => toAddress(it));
    }
    resendVerificationCode(command) {
        return withErrorParsing(this.api.resendVerificationCode(command.addressId));
    }
    async update(command) {
        const patched = await withErrorParsing(this.api.patch(command.addressId, {
            value: command.value,
        }));
        return toAddress(patched);
    }
    async verify(command) {
        const verified = await withErrorParsing(this.api.verify(command.addressId, {
            code: command.code,
        }));
        return toAddress(verified);
    }
}
export class DataServiceWalletDappAddresses {
    constructor(api) {
        this.api = api;
    }
    async create(command) {
        const created = await withErrorParsing(this.api.create({
            addressId: command.addressId,
            dappPublicKey: command.dappAccountAddress.toString(),
            enabled: command.enabled,
        }));
        return toDappAddress(created);
    }
    delete(command) {
        return withErrorParsing(this.api.delete(command.dappAddressId));
    }
    async find(query) {
        try {
            const found = await withErrorParsing(this.api.find(query.dappAddressId));
            return toDappAddress(found);
        }
        catch (e) {
            const err = e;
            if (err instanceof ResourceNotFoundError)
                return null;
            throw e;
        }
    }
    async findAll(query) {
        var _a;
        const found = await withErrorParsing(this.api.findAll(query && {
            addressIds: query.addressIds,
            dappPublicKey: (_a = query.dappAccountAddress) === null || _a === void 0 ? void 0 : _a.toString(),
        }));
        return found.map((it) => toDappAddress(it));
    }
    async update(command) {
        const found = await withErrorParsing(this.api.patch(command.dappAddressId, {
            enabled: command.enabled,
        }));
        return toDappAddress(found);
    }
}
export class DataServiceWalletMessages {
    constructor(api) {
        this.api = api;
        this.textSerde = new UnencryptedTextSerde();
    }
    async findAllFromDapps(query) {
        const dappMessages = await withErrorParsing(this.api.findAllDappMessages({
            skip: query === null || query === void 0 ? void 0 : query.skip,
            take: query === null || query === void 0 ? void 0 : query.take,
            dappVerified: query === null || query === void 0 ? void 0 : query.dappVerified,
        }));
        return dappMessages.map((it) => ({
            author: it.owner,
            timestamp: new Date(it.timestamp),
            text: this.textSerde.deserialize(new Uint8Array(it.text)),
        }));
    }
    async dappMessagesSummary(query) {
        const summaryDto = await withErrorParsing(this.api.dappMessagesSummary({
            dappVerified: query === null || query === void 0 ? void 0 : query.dappVerified,
        }));
        return {
            unreadMessagesCount: summaryDto.unreadMessagesCount,
        };
    }
    async markAllDappMessagesAsRead(command) {
        await withErrorParsing(this.api.markAllDappMessagesAsRead({
            dappVerified: command === null || command === void 0 ? void 0 : command.dappVerified,
        }));
    }
}
function toAddress(addressDto) {
    return {
        id: addressDto.id,
        value: addressDto.value,
        verified: addressDto.verified,
        type: toAddressType(addressDto.type),
        wallet: {
            address: addressDto.wallet.publicKey,
        },
    };
}
export class DataServiceWalletNotificationSubscriptions {
    constructor(api) {
        this.api = api;
    }
    async findAll(query) {
        var _a;
        const dtos = await withErrorParsing(this.api.findAll({
            dappPublicKey: (_a = query === null || query === void 0 ? void 0 : query.dappAccountAddress) === null || _a === void 0 ? void 0 : _a.toString(),
        }));
        return dtos.map(fromNotificationSubscriptionDto);
    }
    async upsert(command) {
        const dto = await withErrorParsing(this.api.upsert(command));
        return fromNotificationSubscriptionDto(dto);
    }
}
function fromNotificationSubscriptionDto(dto) {
    return {
        notificationType: dto.notificationType,
        subscription: {
            wallet: {
                address: dto.subscription.wallet.publicKey,
            },
            config: dto.subscription.config,
        },
    };
}
export class DataServiceWalletPushNotificationSubscriptions {
    constructor(api) {
        this.api = api;
    }
    async delete(physicalId) {
        await withErrorParsing(this.api.delete(physicalId));
    }
    async upsert(command) {
        const dto = await this.api.upsert(command);
        return {
            ...dto,
            walletAddress: dto.walletPublicKey,
        };
    }
    async get(physicalId) {
        const dto = await this.api.get(physicalId);
        return {
            ...dto,
            walletAddress: dto.walletPublicKey,
        };
    }
}
//# sourceMappingURL=data-service-wallets.js.map